import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getAccessToken, removeAccessToken, setAccessToken } from "@/lib/api/client";
import { installAuthInterceptor } from "@/lib/api/install-auth-interceptor";
import { startProactiveRefresh } from "@/lib/api/token-refresh-scheduler";
import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";

import {
  AppRole,
  PermissionName,
  ROLE_PERMISSIONS,
  PERMISSION_CATALOG,
  hasAnyRole,
} from "@/lib/rbac";
import { hasSuperAdminRole } from "@/lib/rbac";

export interface User {
  id: string;
  email: string;
  username: string;
}

interface AuthContextValue {
  user: User | null;
  roles: AppRole[];
  permissions: PermissionName[]; // Keeping for compatibility, though backend uses Roles
  loading: boolean;
  isAdmin: boolean;
  /** Principal Supervisor — gates the staff-monitoring pages. */
  isSuperAdmin: boolean;
  isTeamLeader: boolean;
  isContentCreator: boolean;
  isStudent: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  hasPermission: (p: PermissionName) => boolean;
  hasRole: (r: AppRole) => boolean;

  // ── SuperAdmin impersonation ───────────────────────────────────────────
  /** True while this session is a SuperAdmin viewing the platform as someone else. */
  isImpersonating: boolean;
  /** The SuperAdmin behind an impersonated session, from the token's ImpersonatedBy claim. */
  impersonatedBy: string | null;
  /** Display name of the user being impersonated, for the banner. Null outside an impersonation. */
  impersonatedUserName: string | null;
  /**
   * Starts impersonating a user. Resolves to an error message, or null on success.
   *
   * Note the roles in context become the TARGET's afterwards — including losing isSuperAdmin — because
   * the whole point is to see exactly what they see. Any UI that needs to stay visible during an
   * impersonation must key off `isImpersonating`, not off an admin role.
   */
  impersonate: (userId: string) => Promise<string | null>;
  /** Ends the impersonation and restores the SuperAdmin session. Resolves to an error message, or null. */
  stopImpersonation: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Decodes a JWT token safely without external libraries.
 */
function decodeJwt(token: string) {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode JWT:", e);
    return null;
  }
}

/**
 * Ends the signing-out user's server-side push subscription for THIS browser.
 *
 * Entirely best-effort and entirely silent. Sign-out must never be blocked or made to fail by an
 * optional notification channel — a user who clicked "log out" gets logged out whatever happens here.
 *
 * The push modules are imported dynamically so that sign-out is the only thing that pulls them in, and
 * so this file keeps no static dependency on browser-only APIs (it is imported during SSR).
 */
async function revokePushSubscriptionOnSignOut(): Promise<void> {
  try {
    const { isPushSupported, getExistingPushRegistration } = await import("@/lib/push/service-worker");
    if (!isPushSupported()) return;

    const registration = await getExistingPushRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;

    const { deletePushSubscription } = await import("@/lib/api/push-subscriptions");
    await deletePushSubscription(subscription.endpoint);
  } catch {
    // Nothing here is worth a message to a user who is leaving. If the revoke is missed, the next push
    // to this endpoint still reaches a browser the user has logged out of — which is why the backend
    // also transfers the endpoint on re-registration rather than relying on this call.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  // Read from the token's own ImpersonatedBy claim rather than tracked separately, so it can never
  // disagree with the token actually being sent.
  const [impersonatedBy, setImpersonatedBy] = useState<string | null>(null);
  const [impersonatedUserName, setImpersonatedUserName] = useState<string | null>(null);

  // Load auth state from local storage token
  const loadAuthState = () => {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      setRoles([]);
      setImpersonatedBy(null);
      setImpersonatedUserName(null);
      setLoading(false);
      return;
    }
    if (typeof window !== "undefined" && !localStorage.getItem("accessToken")) {
      localStorage.setItem("accessToken", token);
    }

    const payload = decodeJwt(token);

    // Check expiration
    // An expired token no longer means "signed out" — the interceptor can usually renew it silently
    // from the refresh cookie. Clearing state here would flash the login screen while that is in
    // flight, so the token is kept and the first API call drives the refresh. An impersonation token
    // is the exception: it is deliberately not renewable, so an expired one really is the end.
    if (payload && payload.exp && payload.exp * 1000 < Date.now() && payload.ImpersonatedBy) {
      removeAccessToken();
      setUser(null);
      setRoles([]);
      setImpersonatedBy(null);
      setImpersonatedUserName(null);
      setLoading(false);
      return;
    }

    if (payload) {
      // Map ASP.NET Core identity claims to our User model
      // Note: ASP.NET Core usually puts ID in 'sub' or standard identifier claims
      const userId =
        payload.sub ||
        payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] ||
        payload.uid ||
        "";

      const email =
        payload.email ||
        payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"] ||
        "";

      const username =
        payload.username ||
        payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"] ||
        "";

      // Extract roles (ASP.NET Core uses a specific array claim for roles, or single string)
      let parsedRoles: string[] = [];
      const roleClaim =
        payload.roles || payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

      if (Array.isArray(roleClaim)) {
        parsedRoles = roleClaim;
      } else if (typeof roleClaim === "string") {
        parsedRoles = [roleClaim];
      }

      setUser({ id: userId, email, username });
      setRoles(parsedRoles);
      setImpersonatedBy(payload.ImpersonatedBy ?? null);
      setImpersonatedUserName(payload.ImpersonatedUserName ?? null);
    }

    setLoading(false);
  };

  useEffect(() => {
    // Must be installed before any API call so the ~28 modules that use raw fetch also get the
    // 401-then-refresh retry. Idempotent, so Strict Mode's double-invoke is harmless.
    installAuthInterceptor();

    // Renew the access token proactively at ~80% of its lifetime so that backgrounded tabs never
    // wake up and fan out requests with an expired token. Returns a stop function that tears down
    // the timer and all listeners it owns.
    const stopRefresh = startProactiveRefresh();

    loadAuthState();

    // Listen for custom global events (e.g. from the fetch client 401 interceptor)
    const handleUnauthorized = () => {
      // The dispatchers (interceptor, fetchApi) already call removeAccessToken() before firing this
      // event, but clearing here too is a cheap guarantee: a future dispatcher that forgets would
      // otherwise leave a dead token in localStorage, making the login page redirect loop.
      removeAccessToken();
      setUser(null);
      setRoles([]);
      setImpersonatedBy(null);
      setImpersonatedUserName(null);
    };

    // Listen for custom login success event to reload state without full refresh
    const handleLoginSuccess = () => {
      loadAuthState();
    };

    // An impersonation token expired. The SuperAdmin's own session is still valid underneath, so the
    // honest thing is to drop the impersonation and put them back to their own account rather than
    // logging them out of a session they never actually lost.
    const handleImpersonationExpired = () => {
      void stopImpersonation();
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    window.addEventListener("auth:login-success", handleLoginSuccess);
    window.addEventListener("auth:impersonation-expired", handleImpersonationExpired);

    return () => {
      stopRefresh();
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
      window.removeEventListener("auth:login-success", handleLoginSuccess);
      window.removeEventListener("auth:impersonation-expired", handleImpersonationExpired);
    };
  }, []);

  const impersonate = async (userId: string): Promise<string | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/impersonate/${userId}`, {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
      });

      const body = await res.json().catch(() => null);
      if (!res.ok) return body?.message ?? "Could not start impersonation.";
      if (!body?.token) return "The server did not return an impersonation token.";

      // Swapping the token is all that is needed — loadAuthState re-derives user, roles and the
      // impersonation flag from the new token's claims.
      setAccessToken(body.token);
      loadAuthState();
      return null;
    } catch {
      return "Could not reach the server to start impersonation.";
    }
  };

  const stopImpersonation = async (): Promise<string | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/stop-impersonation`, {
        method: "POST",
        headers: getAuthHeaders(),
        // Sends the SuperAdmin's refresh cookie so the restored session gets a rotated one back.
        credentials: "include",
      });

      const body = await res.json().catch(() => null);

      if (!res.ok) {
        // The admin account is gone, suspended, or demoted — there is genuinely nothing to restore,
        // so a full sign-out is the only correct outcome.
        removeAccessToken();
        setUser(null);
        setRoles([]);
        setImpersonatedBy(null);
      setImpersonatedUserName(null);
        window.location.replace("/login");
        return body?.message ?? "Your administrator session could not be restored.";
      }

      setAccessToken(body.token);
      loadAuthState();
      return null;
    } catch {
      return "Could not reach the server to exit impersonation.";
    }
  };

  // Enterprise role model (mirrors the backend AppRoles set). Roles are additive — a user may
  // hold more than one — so permissions are unioned, not matched exclusively. Legacy names
  // (admin/superadmin/teamleader) are kept as fallbacks during the transition.
  // NOTE: hasAnyRole normalizes "SuperAdmin" to "SystemAdmin", so a SuperAdmin satisfies this too —
  // which is correct, they inherit every SystemAdmin permission.
  const isSystemAdmin = hasAnyRole(roles, "SystemAdmin");
  // The Principal Supervisor distinction, from the raw claim. Gates the staff-monitoring pages only.
  const isSuperAdmin = hasSuperAdminRole(roles);
  const isContentCreator = hasAnyRole(roles, "ContentCreator");
  const isTeamLeader = hasAnyRole(roles, "TeamLeader");
  const isStudent = hasAnyRole(roles, "Student");

  const isAdmin = isSystemAdmin;

  // Populate permissions dynamically based on the union of the user's roles.
  const permissionSet = new Set<PermissionName>();
  const grant = (...ps: PermissionName[]) => ps.forEach((p) => permissionSet.add(p));

  // Union the granted permissions from ROLE_PERMISSIONS for whichever roles the user holds
  // (legacy role-name fallbacks are handled by the isX booleans above).
  if (isSystemAdmin) grant(...ROLE_PERMISSIONS.SystemAdmin);
  if (isContentCreator) grant(...ROLE_PERMISSIONS.ContentCreator);
  if (isTeamLeader) grant(...ROLE_PERMISSIONS.TeamLeader);

  const permissions: PermissionName[] = Array.from(permissionSet);

  const value: AuthContextValue = {
    user,
    roles: roles as AppRole[],
    permissions,
    loading,
    isAdmin,
    isSuperAdmin,
    isTeamLeader,
    isContentCreator,
    isStudent,
    isAuthenticated: !!user,
    signOut: async () => {
      // Give up this user's claim on the browser's push subscription BEFORE the token goes, because the
      // revoke is authenticated and the backend scopes it to the caller — which is also what makes it
      // safe: it cannot touch anybody else's row even though the endpoint is shared with whoever uses
      // this browser next.
      //
      // The BROWSER subscription is deliberately left in place. A push endpoint belongs to a browser
      // profile, not to an LMS account, and the backend already handles an endpoint being re-registered
      // by a different user (it transfers the row and audits it). Tearing it down here would fight that
      // design and cost the next user a fresh permission round-trip for no benefit.
      await revokePushSubscriptionOnSignOut();

      // The refresh token is a server-side row now, so signing out has to tell the server: dropping
      // the local access token alone would leave a live refresh cookie able to mint new sessions.
      // Awaited (unlike before, when there was nothing to await) but never allowed to block the
      // sign-out — if the call fails, the user still gets logged out locally.
      try {
        await fetch(`${API_BASE_URL}/Auth/logout`, {
          method: "POST",
          headers: getAuthHeaders(),
          credentials: "include",
        });
      } catch {
        // Network failure must not trap the user in a session they asked to leave.
      }

      removeAccessToken();
      setUser(null);
      setRoles([]);
      setImpersonatedBy(null);
      setImpersonatedUserName(null);
      window.location.replace("/login");
    },
    hasPermission: (p) => permissions.includes(p),
    hasRole: (r) => hasAnyRole(roles, r),

    isImpersonating: !!impersonatedBy,
    impersonatedBy,
    impersonatedUserName,
    impersonate,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
